import type { ReactNode } from 'react';

/**
 * design-system/06-Components/07-Overlays.md §4 — a question or notice that carries actions, floating just
 * above the bottom navigation.
 * Mirrors android/…/Components/AnchoredPrompt.kt and ios/MysliKrok/Components/AnchoredPrompt.swift.
 *
 * **The page behind it does not move.** Rendered inline, these pushed the content down: asking „Chceš
 * skončiť a vrátiť sa k rodičovi?" shoved „Moje ocenenia" and everything under it down the screen, so the
 * answer appeared somewhere other than where the question had been (R0099).
 *
 * There is no scrim and it does not block. It is not a dialog (§2): the screen behind stays readable and
 * the prompt waits.
 */
export function AnchoredPrompt({
  text,
  isChildMode = false,
  children,
}: {
  text: string;
  /** Larger targets and stacked actions, per §4. */
  isChildMode?: boolean;
  /** At most two actions. */
  children?: ReactNode;
}) {
  return (
    <div
      // A live region, and deliberately not a `dialog`: it must not trap focus. A floating prompt that
      // steals the keyboard is worse than a banner that pushed.
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        // Clear of the navigation, which stays visible and in place underneath.
        bottom: `calc(var(--touch-parent) + var(--space-4) + var(--space-4))`,
        display: 'flex',
        justifyContent: 'center',
        padding: '0 var(--space-4)',
        pointerEvents: 'none',
        zIndex: 20,
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: 'var(--column)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-l)',
          boxShadow: '0 6px 24px rgba(15, 23, 42, 0.18)',
          padding: isChildMode ? 'var(--space-5)' : 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <span style={{ fontSize: '15px', lineHeight: '22px', color: 'var(--text-primary)' }}>{text}</span>

        {children !== undefined ? (
          <div
            style={{
              display: 'flex',
              flexDirection: isChildMode ? 'column' : 'row',
              gap: 'var(--space-3)',
            }}
          >
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
}
